import { useState } from 'react';
import { useUser } from '../context/UserContext';
import { useCommentsQuery } from '../hooks/queries';
import { useCreateComment, useUpdateComment, useDeleteComment } from '../hooks/mutations';
import type { Verse } from '../types';

interface CommentPanelProps {
  selectedVerse: Verse & { reference: string };
  onClose: () => void;
  canonicalBookId: number;
  chapterNumber: number;
}

export default function CommentPanel({ selectedVerse, onClose, canonicalBookId, chapterNumber }: CommentPanelProps) {
  const { isAdmin } = useUser();
  const [editText, setEditText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verseNumber = selectedVerse?.verseNumber;

  const { data: comments, isLoading: loading } = useCommentsQuery(
    canonicalBookId,
    chapterNumber,
    verseNumber,
    !!canonicalBookId && !!chapterNumber && !!verseNumber
  );

  const comment = Array.isArray(comments) && comments.length > 0 ? comments[0] : null;

  const createComment = useCreateComment();
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();

  const saving = createComment.isPending || updateComment.isPending;

  const handleAdd = () => {
    setEditText('');
    setIsEditing(true);
  };

  const handleEdit = () => {
    setEditText(comment?.text || '');
    setIsEditing(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editText.trim()) {
      setError('Please enter a comment');
      return;
    }

    setError(null);

    try {
      if (comment) {
        await updateComment.mutateAsync({
          id: comment.id,
          text: editText,
          canonicalBookId,
          chapterNumber,
          verseNumber,
        });
      } else {
        await createComment.mutateAsync({
          canonicalBookId,
          chapterNumber,
          verseNumber,
          author: 'admin',
          text: editText,
        });
      }
      setIsEditing(false);
      setEditText('');
    } catch (err) {
      console.error('Failed to save comment:', (err as Error).message);
      setError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!comment || !window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await deleteComment.mutateAsync({
        id: comment.id,
        canonicalBookId,
        chapterNumber,
        verseNumber,
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to delete comment:', (err as Error).message);
      setError((err as Error).message);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText('');
    setError(null);
  };

  return (
    <div className="w-full lg:w-80 rounded-lg p-4 space-y-4 bg-white border border-gray-200 dark:bg-ink-800 dark:border-ink-600">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-parchment-100">
          Comentarii - {selectedVerse?.reference || 'No verse selected'}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Comment Display */}
      <div>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Loading...</p>
        ) : comment && !isEditing ? (
          <div className="rounded p-3 bg-parchment-100 border border-parchment-300 dark:bg-ink-700 dark:border-ink-600">
            <p className="text-sm text-gray-700 dark:text-gray-300">{comment.text}</p>
          </div>
        ) : !isEditing ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Comentariul va fi adaugat in curand.</p>
        ) : null}
      </div>

      {/* Admin Actions */}
      {isAdmin() && !loading && !isEditing && (
        <div className="flex gap-2">
          {comment ? (
            <>
              <button
                onClick={handleEdit}
                className="flex-1 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </>
          ) : (
            <button
              onClick={handleAdd}
              className="flex-1 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Add Comment
            </button>
          )}
        </div>
      )}

      {/* Edit/Add Form */}
      {isEditing && (
        <form onSubmit={handleSave} className="space-y-3 border-t border-gray-200 dark:border-ink-600 pt-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/40 dark:text-red-400 p-2 rounded">
              {error}
            </div>
          )}

          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            placeholder="Add a comment..."
            rows={3}
            className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-parchment-400 bg-white border border-gray-300 text-gray-900 dark:bg-ink-700 dark:border-ink-500 dark:text-parchment-100"
          />

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-600/50 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-colors border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-ink-500 dark:text-parchment-100 dark:hover:bg-ink-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
